/**
 * useWithingsData - Shared Hook für Withings Data Management
 * Zentrale Logik für alle Withings Route-Komponenten
 */

import { core } from "@tauri-apps/api";
import { useEffect, useState, useCallback } from "react";

import { WithingsSmartSyncService } from "../services/withingsSmartSync";
import {
  type WithingsMeasurement,
  type SyncStatus,
  type SyncResult,
  type DateRange,
} from "../types";
import { useBackgroundRefresh } from "./useBackgroundRefresh";
import { useWithingsAuth } from "./useWithingsAuth";

export const useWithingsData = () => {
  const { disconnect, isAuthenticated, isInitialized } = useWithingsAuth();

  // Initialize background refresh service
  useBackgroundRefresh();

  const [measurements, setMeasurements] = useState<WithingsMeasurement[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    daysSinceLastSync: null,
    lastSyncDate: null,
    totalMeasurements: 0,
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoadingMeasurements, setIsLoadingMeasurements] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | undefined>();
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [dateRangeStart, setDateRangeStart] = useState("2025-01-01");
  const [dateRangeEnd, setDateRangeEnd] = useState(new Date().toISOString().split("T")[0]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const status = await WithingsSmartSyncService.getSyncStatus();
      setSyncStatus(status);
    } catch {
      // NOTE: Failed to load sync status - silently ignore
    }
  }, []);

  const loadMeasurements = useCallback(async () => {
    try {
      setIsLoadingMeasurements(true);
      let days = 365;
      switch (dateRange) {
        case "7d": {
          days = 7;

          break;
        }
        case "30d": {
          days = 30;

          break;
        }
        case "90d": {
          days = 90;

          break;
        }
        // No default
      }

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);

      const data = await core.invoke("get_withings_measurements", {
        end_date: endDate.toISOString().split("T")[0],
        limit: days,
        start_date: startDate.toISOString().split("T")[0],
      });

      // Ensure data is an array
      const measurementsArray = Array.isArray(data) ? data : [];

      setMeasurements(measurementsArray);
    } catch {
      // Failed to load measurements
    } finally {
      setIsLoadingMeasurements(false);
    }
  }, [dateRange]);

  const handleSmartSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const result = await WithingsSmartSyncService.smartSync();
      setSyncResult(result);
      if (result.success) {
        await loadSyncStatus();
        await loadMeasurements();
      }
    } catch {
      // Smart sync failed
    } finally {
      setIsSyncing(false);
    }
  }, [loadSyncStatus, loadMeasurements]);

  // Callback für Sync-Button (wird nach erfolgreichem Sync aufgerufen)
  const onSyncComplete = useCallback(async () => {
    await loadSyncStatus();
    await loadMeasurements();
  }, [loadSyncStatus, loadMeasurements]);

  const handleForceSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const result = await WithingsSmartSyncService.forceFullSync();
      setSyncResult(result);
      if (result.success) {
        await loadSyncStatus();
        await loadMeasurements();
      }
    } catch {
      // Force sync failed
    } finally {
      setIsSyncing(false);
    }
  }, [loadSyncStatus, loadMeasurements]);

  const handleDateRangeSync = useCallback(
    async (start?: string, end?: string) => {
      const startDateStr = start ?? dateRangeStart;
      const endDateStr = end ?? dateRangeEnd;
      if (!startDateStr || !endDateStr) {
        alert("Bitte wähle Start- und Enddatum aus.");
        return;
      }

      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);

      if (startDate > endDate) {
        alert("Startdatum muss vor dem Enddatum liegen.");
        return;
      }

      setIsSyncing(true);
      try {
        const result = await WithingsSmartSyncService.syncDateRange(startDate, endDate);
        setSyncResult(result);
        if (result.success) {
          await loadSyncStatus();
          await loadMeasurements();
          setShowDateRangeModal(false);
        }
      } catch {
        // Date range sync failed
      } finally {
        setIsSyncing(false);
      }
    },
    [dateRangeStart, dateRangeEnd, loadSyncStatus, loadMeasurements],
  );

  // Load sync status on mount
  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      void loadSyncStatus();
    }
  }, [isAuthenticated, isInitialized, loadSyncStatus]);

  // Load measurements when dateRange changes or on mount
  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      void loadMeasurements();
    }
  }, [isAuthenticated, isInitialized, dateRange, loadMeasurements]);

  return {
    dateRange,
    dateRangeEnd,
    dateRangeStart,
    disconnect,
    handleDateRangeSync,
    handleForceSync,
    handleSmartSync,
    // Auth state
    isAuthenticated,
    isInitialized,

    isLoadingMeasurements,
    isSyncing,
    // Data
    measurements,
    onSyncComplete,
    // Actions
    setDateRange,
    setDateRangeEnd,
    setDateRangeStart,
    setShowDateRangeModal,
    showDateRangeModal,

    syncResult,
    syncStatus,
  };
};
